import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import type { AxiosError } from 'axios';
import { financeApi } from '@api/financeApi';
import type {
  InvoiceFilters,
  CreateInvoiceRequest,
  RecordPaymentRequest,
} from '@/types/finance';

// Сервер повертає причину відмови в полі error (напр. «замовлення не знайдено») — показуємо її
function mdServerErrorText(mdError: unknown, mdFallback: string): string {
  const mdText = (mdError as AxiosError<{ error?: string }>)?.response?.data?.error;
  return mdText ? `${mdFallback}: ${mdText}` : mdFallback;
}

// ── Invoices ──────────────────────────────────────────────

export function useInvoices(mdFilters: InvoiceFilters) {
  return useQuery({
    queryKey: ['finance', 'invoices', mdFilters],
    queryFn: () => financeApi.getInvoices(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useInvoiceById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['finance', 'invoices', mdId],
    queryFn: () => financeApi.getInvoiceById(mdId!),
    enabled: !!mdId,
  });
}

export function useCreateInvoice() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateInvoiceRequest) =>
      financeApi.createInvoice(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      message.success('Рахунок створено');
    },
    onError: (mdError) => {
      message.error(mdServerErrorText(mdError, 'Помилка при створенні рахунку'));
    },
  });
}

export function useIssueInvoice() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => financeApi.issueInvoice(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      message.success('Рахунок виставлено');
    },
    onError: () => {
      message.error('Помилка при виставленні рахунку');
    },
  });
}

export function useCancelInvoice() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { id: string; reason: string }) =>
      financeApi.cancelInvoice(mdParams.id, mdParams.reason),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      message.success('Рахунок скасовано');
    },
    onError: () => {
      message.error('Помилка при скасуванні рахунку');
    },
  });
}

export function useRecordPayment() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: RecordPaymentRequest) =>
      financeApi.recordPayment(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      message.success('Оплату записано');
    },
    onError: () => {
      message.error('Помилка при записі оплати');
    },
  });
}

// ── Accounts Receivable ──────────────────────────────────

export function useArSummary() {
  return useQuery({
    queryKey: ['finance', 'ar', 'summary'],
    queryFn: () => financeApi.getArSummary(),
  });
}
